/**
 * =====================================================
 * Mining Survey WebGIS
 * Camera Module
 * =====================================================
 */
import AppState from "../../core/app.js";
import piexif from "piexifjs";
import proj4 from "proj4";

class Camera {

    constructor() {

        this.video = null;
        this.overlay = null;

        this.button = null;
        this.closeButton = null;
        this.captureButton = null;

        this.stream = null;
        this.active = false;

        this.canvas = null;
        this.imageData = null;

        this.photoMetadata = null;
    }


    /* =====================================================
       INIT
       ===================================================== */

    init() {

        this.button =
            document.getElementById("btnCamera");

        this.closeButton =
            document.getElementById("btnCameraClose");

        this.captureButton =
            document.getElementById("btnCameraCapture");

        this.video =
            document.getElementById("cameraVideo");

        this.overlay =
            document.getElementById("cameraOverlay");


        if (!this.button) {

            console.warn(
                "[Camera] Button not found"
            );

            return;
        }


        this.button.addEventListener(
            "click",
            () => {
                this.open();
            }
        );


        if (this.closeButton) {

            this.closeButton.addEventListener(
                "click",
                () => {
                    this.close();
                }
            );

        }


        if (this.captureButton) {

            this.captureButton.addEventListener(
                "click",
                () => {
                    this.capture();
                }
            );

        }


        console.log(
            "[Camera] Initialized"
        );
    }


    /* =====================================================
       OPEN
       ===================================================== */

    async open() {

        if (this.active) {
            return;
        }


        console.log(
            "[Camera] Opening..."
        );


        try {

            this.stream =
                await navigator.mediaDevices
                    .getUserMedia({

                        video: {

                            facingMode: {
                                ideal: "environment"
                            },

                            width: {
                                ideal: 1920
                            },

                            height: {
                                ideal: 1080
                            }

                        },

                        audio: false

                    });


            this.video.srcObject =
                this.stream;


            this.overlay
                ?.classList
                .add("show");


            this.active = true;


            console.log(
                "[Camera] Preview aktif."
            );

        }
        catch (error) {

            console.error(
                "[Camera] Gagal membuka kamera:",
                error
            );

        }
    }


    /* =====================================================
       CAPTURE
       ===================================================== */

    capture() {

        if (
            !this.video ||
            !this.active
        ) {
            return;
        }


        if (
            !this.video.videoWidth ||
            !this.video.videoHeight
        ) {

            console.warn(
                "[Camera] Video not ready"
            );

            return;
        }


        /*
         * Create canvas
         */

        this.canvas =
            document.createElement("canvas");


        this.canvas.width =
            this.video.videoWidth;

        this.canvas.height =
            this.video.videoHeight;


        const ctx =
            this.canvas.getContext("2d");


        /*
         * Capture current video frame
         */

        ctx.drawImage(
            this.video,
            0,
            0,
            this.canvas.width,
            this.canvas.height
        );


        /*
        * =====================================================
        * GET GPS METADATA
        * =====================================================
        */

        const position =
            AppState.currentPosition;


        if (position) {

            this.photoMetadata = {

                latitude:
                    position.latitude,

                longitude:
                    position.longitude,

                accuracy:
                    position.accuracy ?? null,

                altitude:
                    position.altitude ?? null,

                heading:
                    position.heading ?? null,

                speed:
                    position.speed ?? null,

                timestamp:
                    position.timestamp ??
                    Date.now()

            };

        }
        else {

            this.photoMetadata = null;

        }


        console.log(
            "[Camera] Photo metadata:",
            this.photoMetadata
        );


        /*
        * =====================================================
        * WATERMARK
        * =====================================================
        */

        this.drawWatermark();


        /*
        * =====================================================
        * CONVERT TO JPEG
        * =====================================================
        */

        let jpegData =
            this.canvas.toDataURL(
                "image/jpeg",
                0.92
            );


        /*
        * =====================================================
        * ADD EXIF GPS
        * =====================================================
        */

        if (this.photoMetadata) {

            jpegData =
                this.addExifGPS(
                    jpegData,
                    this.photoMetadata
                );

        }


        this.imageData =
            jpegData;
        
        this.showPreview();
    }


        /* =====================================================
   DRAW WATERMARK
    ===================================================== */

    drawWatermark() {

        if (
            !this.canvas ||
            !this.photoMetadata
        ) {
            return;
        }


        const ctx =
            this.canvas.getContext("2d");


        const width =
            this.canvas.width;

        const height =
            this.canvas.height;


        /*
        * =====================================================
        * WGS84 GPS
        * =====================================================
        */

        const latitude =
            Number(
                this.photoMetadata.latitude
            );

        const longitude =
            Number(
                this.photoMetadata.longitude
            );


        if (
            !Number.isFinite(latitude) ||
            !Number.isFinite(longitude)
        ) {

            console.warn(
                "[Camera] Invalid GPS coordinates."
            );

            return;
        }


        /*
        * =====================================================
        * WGS84 → UTM ZONE 50N
        * EPSG:4326 → EPSG:32650
        * =====================================================
        */

        const [easting, northing] =
            proj4(
                "EPSG:4326",
                "EPSG:32650",
                [
                    longitude,
                    latitude
                ]
            );


        console.log(
            "[Camera] UTM:",
            {
                easting,
                northing
            }
        );


        /*
        * =====================================================
        * DATE / TIME
        * =====================================================
        */

        const timestamp =
            this.photoMetadata.timestamp ??
            Date.now();


        const date =
            new Date(timestamp);


        const pad =
            value =>
                String(value).padStart(2, "0");


        const year =
            date.getFullYear();


        const dateText =
            `${year}-` +
            `${pad(date.getMonth() + 1)}-` +
            `${pad(date.getDate())}`;


        const timeText =
            `${pad(date.getHours())}:` +
            `${pad(date.getMinutes())}:` +
            `${pad(date.getSeconds())}`;


        /*
        * =====================================================
        * WATERMARK TEXT
        * =====================================================
        */

        const line1 =
            "WGS'84";


        const line2 =
            `Lat : ${latitude.toFixed(8)} | Lon : ${longitude.toFixed(8)}`;


        const line3 =
            "UTM Zona 50N";


        const line4 =
            `E : ${easting.toFixed(2)} | N : ${northing.toFixed(2)}`;


        const line5 =
            `Tanggal : ${dateText} ${timeText}`;


        const line6 =
            `©minegis|Survey`;


        /*
        * =====================================================
        * WATERMARK SIZE
        * =====================================================
        */

        const padding =
            Math.max(
                12,
                Math.round(width * 0.018)
            );


        const fontSize =
            Math.max(
                13,
                Math.round(width * 0.022)
            );


        const titleFontSize =
            Math.max(
                14,
                Math.round(width * 0.024)
            );


        const lineHeight =
            fontSize * 1.45;


        const titleLineHeight =
            titleFontSize * 1.45;


        const gap =
            Math.max(
                4,
                Math.round(fontSize * 0.25)
            );


        /*
        * 6 baris
        */

        const watermarkHeight =
            padding * 2 +
            titleLineHeight +
            lineHeight * 5 +
            gap * 2;


        /*
        * =====================================================
        * BACKGROUND
        * =====================================================
        */

        ctx.save();


        ctx.fillStyle =
            "rgba(0, 0, 0, 0.65)";


        ctx.fillRect(
            0,
            height - watermarkHeight,
            width,
            watermarkHeight
        );


        /*
        * =====================================================
        * TEXT SETTINGS
        * =====================================================
        */

        ctx.textAlign =
            "left";


        ctx.textBaseline =
            "middle";


        ctx.fillStyle =
            "#ffffff";


        const x =
            padding;


        let y =
            height -
            watermarkHeight +
            padding +
            titleLineHeight / 2;


        /*
        * =====================================================
        * LINE 1
        * =====================================================
        */

        ctx.font =
            `700 ${titleFontSize}px Arial`;


        ctx.fillText(
            line1,
            x,
            y
        );


        y +=
            titleLineHeight;


        /*
        * =====================================================
        * LINE 2
        * =====================================================
        */

        ctx.font =
            `600 ${fontSize}px Arial`;


        ctx.fillText(
            line2,
            x,
            y
        );


        y +=
            lineHeight;


        /*
        * =====================================================
        * GAP
        * =====================================================
        */

        y +=
            gap;


        /*
        * =====================================================
        * LINE 3
        * =====================================================
        */

        ctx.font =
            `700 ${titleFontSize}px Arial`;


        ctx.fillText(
            line3,
            x,
            y
        );


        y +=
            titleLineHeight;


        /*
        * =====================================================
        * LINE 4
        * =====================================================
        */

        ctx.font =
            `600 ${fontSize}px Arial`;


        ctx.fillText(
            line4,
            x,
            y
        );


        y +=
            lineHeight;


        /*
        * =====================================================
        * LINE 5
        * =====================================================
        */

        ctx.fillText(
            line5,
            x,
            y
        );


        y +=
            lineHeight;


        /*
        * =====================================================
        * LINE 6
        * =====================================================
        */

        ctx.font =
            `700 ${fontSize}px Arial`;


        ctx.fillText(
            line6,
            x,
            y
        );


        ctx.restore();


        console.log(
            "[Camera] Watermark applied."
        );
    }

    /* =====================================================
        ADD EXIF GPS
        ===================================================== */

        addExifGPS(
            jpegData,
            metadata
        ) {

            try {

                const lat =
                    Number(metadata.latitude);

                const lon =
                    Number(metadata.longitude);

                const altitude =
                    Number(metadata.altitude ?? 0);


                if (
                    !Number.isFinite(lat) ||
                    !Number.isFinite(lon)
                ) {

                    console.warn(
                        "[Camera] Invalid GPS coordinates."
                    );

                    return jpegData;
                }


                /*
                * Convert decimal degree
                * to EXIF DMS
                */

                const toDMS =
                    decimal => {

                        const absolute =
                            Math.abs(decimal);


                        const degrees =
                            Math.floor(
                                absolute
                            );


                        const minutesFloat =
                            (
                                absolute -
                                degrees
                            ) * 60;


                        const minutes =
                            Math.floor(
                                minutesFloat
                            );


                        const seconds =
                            (
                                minutesFloat -
                                minutes
                            ) * 60;


                        return [
                            [degrees, 1],
                            [minutes, 1],
                            [
                                Math.round(
                                    seconds * 10000
                                ),
                                10000
                            ]
                        ];
                    };


                const latRef =
                    lat >= 0
                        ? "N"
                        : "S";


                const lonRef =
                    lon >= 0
                        ? "E"
                        : "W";


                /*
                * EXIF GPS
                */

                const zerothIfd = {

                    [piexif.ImageIFD.Software]:
                        "MiningSurveyWebGIS"

                };


                const exifIfd = {

                    [piexif.ExifIFD.DateTimeOriginal]:
                        this.getExifDate(
                            metadata.timestamp
                        )

                };


                const gpsIfd = {

                    [piexif.GPSIFD.GPSLatitudeRef]:
                        latRef,

                    [piexif.GPSIFD.GPSLatitude]:
                        toDMS(lat),

                    [piexif.GPSIFD.GPSLongitudeRef]:
                        lonRef,

                    [piexif.GPSIFD.GPSLongitude]:
                        toDMS(lon),

                    [piexif.GPSIFD.GPSAltitudeRef]:
                        0,

                    [piexif.GPSIFD.GPSAltitude]:
                        [
                            Math.round(
                                Math.abs(altitude) * 100
                            ),
                            100
                        ]

                };


                const exifObj =
                    {

                        "0th":
                            zerothIfd,

                        "Exif":
                            exifIfd,

                        "GPS":
                            gpsIfd,

                        "1st":
                            {}

                    };


                const exifBytes =
                    piexif.dump(
                        exifObj
                    );


                const result =
                    piexif.insert(
                        exifBytes,
                        jpegData
                    );


                console.log(
                    "[Camera] EXIF GPS applied."
                );


                console.log(
                    "[Camera] EXIF:",
                    {
                        latitude: lat,
                        longitude: lon,
                        altitude: altitude,
                        latitudeRef: latRef,
                        longitudeRef: lonRef
                    }
                );


                return result;

            }
            catch (error) {

                console.error(
                    "[Camera] EXIF GPS error:",
                    error
                );

                return jpegData;
            }
        }

        /* =====================================================
   EXIF DATE
   ===================================================== */

        getExifDate(timestamp) {

            const date =
                new Date(
                    timestamp ?? Date.now()
                );


            const pad =
                value =>
                    String(value).padStart(2, "0");


            return (
                `${date.getFullYear()}:` +
                `${pad(date.getMonth() + 1)}:` +
                `${pad(date.getDate())} ` +
                `${pad(date.getHours())}:` +
                `${pad(date.getMinutes())}:` +
                `${pad(date.getSeconds())}`
            );
        }
                    /* =====================================================
       SHOW PREVIEW
       ===================================================== */

    showPreview() {

        const video =
            this.video;

        if (video) {

            video.style.display =
                "none";

        }


        /*
         * Find or create preview image
         */

        let preview =
            document.getElementById(
                "cameraPhotoPreview"
            );


        if (!preview) {

            preview =
                document.createElement(
                    "img"
                );

            preview.id =
                "cameraPhotoPreview";

            preview.className =
                "camera-photo-preview";


            this.overlay.appendChild(
                preview
            );

        }


        preview.src =
            this.imageData;


        preview.style.display =
            "block";


        /*
         * Show preview controls
         */

        let controls =
            document.getElementById(
                "cameraPreviewControls"
            );


        if (!controls) {

            controls =
                document.createElement(
                    "div"
                );

            controls.id =
                "cameraPreviewControls";

            controls.className =
                "camera-preview-controls";


            controls.innerHTML = `

                <button
                    id="btnCameraRetake"
                    class="camera-preview-btn camera-retake"
                    type="button">

                    <i class="bi bi-arrow-counterclockwise"></i>

                    <span>Retake</span>

                </button>


                <button
                    id="btnCameraUsePhoto"
                    class="camera-preview-btn camera-use"
                    type="button">

                    <i class="bi bi-check-lg"></i>

                    <span>Use Photo</span>

                </button>

            `;


            this.overlay.appendChild(
                controls
            );


            /*
             * Retake
             */

            document
                .getElementById(
                    "btnCameraRetake"
                )
                .addEventListener(
                    "click",
                    () => {

                        this.retake();

                    }
                );


            /*
             * Use Photo
             */

            document
                .getElementById(
                    "btnCameraUsePhoto"
                )
                .addEventListener(
                    "click",
                    () => {

                        this.usePhoto();

                    }
                );

        }


        controls.style.display =
            "flex";


        /*
         * Hide shutter
         */

        if (this.captureButton) {

            this.captureButton.style.display =
                "none";

        }


        console.log(
            "[Camera] Photo preview aktif."
        );
    }


    /* =====================================================
       RETAKE
       ===================================================== */

    retake() {

        const preview =
            document.getElementById(
                "cameraPhotoPreview"
            );


        if (preview) {

            preview.style.display =
                "none";

        }


        const controls =
            document.getElementById(
                "cameraPreviewControls"
            );


        if (controls) {

            controls.style.display =
                "none";

        }


        /*
         * Show video again
         */

        if (this.video) {

            this.video.style.display =
                "block";

        }


        /*
         * Show shutter again
         */

        if (this.captureButton) {

            this.captureButton.style.display =
                "flex";

        }


        console.log(
            "[Camera] Retake."
        );
    }

    /* =====================================================
    USE PHOTO
    ===================================================== */

    usePhoto() {

        console.log(
            "[Camera] Photo selected."
        );


        if (!this.imageData) {

            console.warn(
                "[Camera] Tidak ada foto untuk disimpan."
            );

            return;
        }


        const link =
            document.createElement("a");


        const now =
            new Date();


        const pad =
            value =>
                String(value).padStart(2, "0");


        const fileName =
            `PHOTO_${now.getFullYear()}` +
            `${pad(now.getMonth() + 1)}` +
            `${pad(now.getDate())}_` +
            `${pad(now.getHours())}` +
            `${pad(now.getMinutes())}` +
            `${pad(now.getSeconds())}.jpg`;


        link.href =
            this.imageData;


        link.download =
            fileName;


        document.body.appendChild(
            link
        );


        link.click();


        document.body.removeChild(
            link
        );


        console.log(
            `[Camera] Photo saved: ${fileName}`
        );


        console.log(
            "[Camera] GPS metadata:",
            this.photoMetadata
        );


        this.close();
    }

    /* =====================================================
       CLOSE
       ===================================================== */

    close() {

        if (this.stream) {

            this.stream
                .getTracks()
                .forEach(
                    track => {
                        track.stop();
                    }
                );

            this.stream = null;
        }


        if (this.video) {

            this.video.srcObject =
                null;

            this.video.style.display =
                "block";
        }


        const preview =
            document.getElementById(
                "cameraPhotoPreview"
            );


        if (preview) {

            preview.remove();

        }


        const controls =
            document.getElementById(
                "cameraPreviewControls"
            );


        if (controls) {

            controls.remove();

        }


        if (this.captureButton) {

            this.captureButton.style.display =
                "flex";

        }


        this.overlay
            ?.classList
            .remove("show");


        this.active = false;


        console.log(
            "[Camera] Camera closed."
        );
    }

}


export default new Camera();